import { SubscriptionType } from 'common/constants/subscription';
import { StringDictionary } from 'common/interfaces/dictionary';
import { objectUtils } from 'common/utils/object-utils';
import { UserInfo } from 'unit-people/interfaces/user-info';
import { SubscriptionPlan } from 'unit-subscription/interfaces/subscription-plan';
import { CommonApi } from '../../api/server';
import { CompaniesC, Company, UpdateCompany } from './interfaces/company';
import { CreateOrganizationFormData } from './interfaces/create-organization-form-data';
import { MotivationPopupSettings, MotivationPopupSettingsC } from './interfaces/motivation-popup-settings';
import { UserInfoResponse } from './interfaces/user-info-response';
import { UserSettings } from './interfaces/user-settings';

function getUserInfo(): Promise<UserInfoResponse> {
  return CommonApi.get('/user/info');
}

function setUserInfo(data: any): Promise<any> {
  return CommonApi.put('/user/info', data);
}

function getUsersInfo(usersIds: string[]): Promise<StringDictionary<UserInfo>> {
  if (!usersIds.length) return Promise.resolve({});

  const query = objectUtils.toQueryString({ userId: usersIds });
  return CommonApi.get(`/user/infos?${query}`);
}

function fetchCompanies(): Promise<Company[]> {
  return CommonApi.getV('/companies', CompaniesC);
}

function updateCompany(companyId: number, data: UpdateCompany): Promise<Company[]> {
  return CommonApi.putV(`/companies/${companyId}`, data, CompaniesC);
}

function createCompany(data: CreateOrganizationFormData): Promise<Company[]> {
  return CommonApi.postV(
    '/companies',
    data,
    CompaniesC,
    'Something wrong while creating a company.');
}

const ablyGetTokenUrl = '/user/ably-token';

function getAblyToken(): Promise<string> {
  return CommonApi.get(ablyGetTokenUrl);
}

function buildLogoPostFix(companyId: number): string {
  return `/companies/${companyId}/logo`;
}

function getCompanyLogoLink(company: Company): string {
  return company.hasLogo ? `/api${buildLogoPostFix(company.id)}` : null;
}

function buildAvatarLinkPostfix(userId: string, hasAvatar: boolean = true): string {
  return hasAvatar ? `/user/${userId}/avatar` : null;
}

function getAvatarLink(user: { id: string, hasAvatar: boolean }): string {
  return user.hasAvatar
    ? `/api${buildAvatarLinkPostfix(user.id)}`
    : null;
}


function getPlanInfo(type: SubscriptionType, planId: string): Promise<SubscriptionPlan> {
  return CommonApi.get(
    `/billing/${type}/plans/info/${planId}`,
    'Error while fetching education plan from backend');
}

function getCheckoutNewSubscriptionHostedPage(planId: string): Promise<ChargebeeHostedPage> {
  return CommonApi.get(
    `/billing/plans/${planId}/checkout-new-subscription-page`,
    'Error while fetching checkout-new-subscription hosted page object');
}

function getPortalSession(): Promise<ChargebeePortalSession> {
  return CommonApi.get('/billing/portal-session', 'Error while fetching portal session object');
}

function acknowledgeSubscriptionChanges(): Promise<void> {
  return CommonApi.post('/billing/acknowledge-changes', null, 'Error while acknowledging subscription changes');
}

function getMotivationPopupSettings(): Promise<MotivationPopupSettings> {
  return CommonApi.getV(
    `/common/subscription-popup-config`,
    MotivationPopupSettingsC,
    'error in load motivation popup settings',
  );
}

function setMotivationPopupSettings(settings: MotivationPopupSettings): Promise<void> {
  return CommonApi.post(
    `/admin/subscription-popup-config`,
    settings,
    'error set motivation popup settings',
  );
}

function deleteMotivationPopupSettings(): Promise<void> {
  return CommonApi.delete(
    `/admin/subscription-popup-config`,
    null,
    'error delete motivation popup settings',
  );
}

function changeSettings(settings: UserSettings): Promise<void> {
  return CommonApi.put('/user/settings', settings);
}

export const AccountApi = {
  createCompany,
  fetchCompanies,
  updateCompany,
  getUserInfo,
  getUsersInfo,
  setUserInfo,
  getAblyToken,
  ablyGetTokenUrl,
  buildLogoPostFix,
  getCompanyLogoLink,
  buildAvatarLinkPostfix,
  getAvatarLink,
  getPlanInfo,
  getCheckoutNewSubscriptionHostedPage,
  getPortalSession,
  acknowledgeSubscriptionChanges,
  getMotivationPopupSettings,
  setMotivationPopupSettings,
  deleteMotivationPopupSettings,
  changeSettings,
};
